export enum IdentifierType {
  Email = 'email',
  MultiIdentifier = 'multi',
}

// These are the workspace identifiers. Workspaces and customers can use these as identifiers,
// and they will always be in sync. That is, if a workspace uses Id and cio_id, but not Email, then
// customer records will be identified by Id and/or cio_id but not Email.
export enum IdentifierKey {
  Id = 'id',
  CioId = 'cio_id',
  Email = 'email',
}

export enum IdentifierState {
  Disabled = 0,
  Enabled = 1,
  EnabledImmutable = 2,
}

export enum ObjectIdentifierKey {
  CioObjectId = 'cio_object_id',
  ObjectId = 'object_id',
}

export enum ObjectTypeIdentifierKey {
  ObjectTypeId = 'object_type_id',
}

export type IdentifierConfig = IdentifierState;

export type Identifiers = Record<IdentifierKey, IdentifierConfig>;

// All identifier types except cio_id can be set to enabled/disabled or mutable/immutable
export type OptionalCustomerIdentifiers = Exclude<IdentifierKey, IdentifierKey.CioId>;

export type CustomerIdentifiers = Partial<Record<OptionalCustomerIdentifiers, string>> & {
  [IdentifierKey.CioId]: string;
};

export type ObjectIdentifiers = Partial<Record<ObjectIdentifierKey, string>>;

export enum SelfServeMigrateTo {
  IdOnly = 'id_only',
  IdMutable = 'id_mutable',
  MultiId = 'multi_id',
}

export const EMAIL_AND_ID_IDENTIFIER_CONFIG: Identifiers = {
  [IdentifierKey.CioId]: IdentifierState.EnabledImmutable,
  [IdentifierKey.Id]: IdentifierState.Enabled,
  [IdentifierKey.Email]: IdentifierState.Enabled,
};

export const ID_ONLY_IDENTIFIER_CONFIG: Identifiers = {
  [IdentifierKey.CioId]: IdentifierState.EnabledImmutable,
  [IdentifierKey.Id]: IdentifierState.EnabledImmutable,
  [IdentifierKey.Email]: IdentifierState.Disabled,
};

export function isAttributeIdentifier(attribute: string, identifiers: Identifiers): attribute is IdentifierKey {
  return attribute in identifiers;
}

export function isObjectAttributeIdentifier(attribute: string) {
  return ([ObjectIdentifierKey.CioObjectId, ObjectIdentifierKey.ObjectId] as string[]).includes(attribute);
}

export function isAttributeEnabledIdentifier(attribute: string | IdentifierKey, identifiers?: Identifiers) {
  if (identifiers != null && isAttributeIdentifier(attribute, identifiers)) {
    return identifiers[attribute] !== IdentifierState.Disabled;
  }

  return isObjectAttributeIdentifier(attribute);
}

export function isAttributeImmutableIdentifier(attribute: string | IdentifierKey, identifiers: Identifiers) {
  if (isAttributeIdentifier(attribute, identifiers)) {
    return identifiers[attribute] === IdentifierState.EnabledImmutable;
  }
  if (attribute === ObjectIdentifierKey.CioObjectId || attribute === ObjectIdentifierKey.ObjectId) {
    return true;
  }
  return false;
}
